import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import DropdownButton from 'components/ui/tailwind/DropdownButton';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import EditReferenceNumbersModal from 'components/modules/shipments/EditReferenceNumbersModal';
import { Checkbox, Label } from '@cargomatic/ui';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import IconTextDisplay from 'components/v2/IconTextDisplay';
import CalendarIcon from 'components/v2/icons/CalendarIcon';
import TruckIcon from 'components/v2/icons/TruckIcon';
import stopIntents from 'config/stop-intent-types';
import SessionStore from 'stores/SessionStore';
import { titleize } from 'underscore.string';
import Location from './Location';
import LoadDistributionModal from './LoadDistributionModal';
import StopAppointmentWindowModal from './StopAppointmentWindowModal';
import {
  getLoadQuantity,
  getLoadWeight,
  getLoadDimensions,
} from '../../../load/Loads';

const dateTimeFormat = 'L LT';

function formatDate(date, timezone, t) {
  if (!date) {
    return t('NOT_SET');
  }

  return moment(date).tz(timezone).format(dateTimeFormat);
}

class Stop extends Component {
  static propTypes = {
    stop: PropTypes.object.isRequired,
    stops: PropTypes.array.isRequired,
    shipment_type: PropTypes.string.isRequired,
    locations: PropTypes.array,
    timezone: PropTypes.string,
    children: PropTypes.object, // drag handle
    onStopFieldChange: PropTypes.func.isRequired,
    onLocationFieldChange: PropTypes.func.isRequired,
    onNameSelect: PropTypes.func.isRequired,
    onAddressSelect: PropTypes.func.isRequired,
    onStartChange: PropTypes.func.isRequired,
    onEndChange: PropTypes.func.isRequired,
    onRemoveLocation: PropTypes.func.isRequired,
    onRemoveStop: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    const isIntermodal = isIntermodalShipmentType({
      shipment_type: props.shipment_type,
    });
    const { t } = this.props;
    const stop_types = (
      isIntermodal
        ? stopIntents
        : stopIntents.filter((i) => !/Empty/.test(i.text))
    ).map((item) => {
      return {
        name: titleize(t(item.localizationSlug)),
        value: item.slug,
      };
    });
    this.state = {
      stop_types,
    };
  }

  handleStopFieldChange = (field, value) => {
    this.props.onStopFieldChange(this.props.stop, field, value);
  };

  handleRequireAppointmentFieldChange = (field, value) => {
    this.props.onStopFieldChange(this.props.stop, field, value);
    /**
     * Why this?
     * If user selects requires appointment,
     * reset the start and end window to null
     */
    if (value) {
      this.props.onEndChange(this.props.stop, null);
      this.props.onStartChange(this.props.stop, null);
    }
  };

  handleLocationFieldChange = (field, value) => {
    this.props.onLocationFieldChange(this.props.stop, field, value);
  };

  handleLocationContactChange = (field, value) => {
    this.props.onLocationContactChange(this.props.stop, field, value);
  };

  handleLocationNameSelect = (selectedLocation) => {
    this.props.onNameSelect(this.props.stop, selectedLocation);
  };

  handleLocationAddressSelect = (item) => {
    this.props.onAddressSelect(this.props.stop, item);
  };

  handleStartChange = (start) => {
    this.props.onStartChange(this.props.stop, start);
  };

  handleEndChange = (end) => {
    this.props.onEndChange(this.props.stop, end);
  };

  handleRemoveStop = () => {
    this.props.onRemoveStop(this.props.stop);
  };

  handleLoadDistributionSave = (newStops) => {
    const { stops, onStopFieldChange } = this.props;
    stops.forEach((stop, i) => {
      onStopFieldChange(stop, 'loads', newStops[i].loads);
    });
    this.hideLoadDistributionModal();
  };

  handleSaveReferenceNumbers = (reference_numbers) => {
    reference_numbers.forEach((refnum) => {
      refnum.stop_id = this.props.stop.id;
    });
    this.handleStopFieldChange('reference_numbers', reference_numbers);
    this.hideRefnumModal();
  };

  showStopAppointmentModal = () => {
    this.setState({ showStopAppointmentModal: true });
  };

  hideStopAppointmentModal = () => {
    this.setState({ showStopAppointmentModal: false });
  };

  showLoadDistributionModal = () => {
    this.setState({ showLoadDistributionModal: true });
  };

  hideLoadDistributionModal = () => {
    this.setState({ showLoadDistributionModal: false });
  };

  showRefnumModal = () => {
    this.setState({ showAddRefnumsModal: true });
  };

  hideRefnumModal = () => {
    this.setState({ showAddRefnumsModal: false });
  };

  renderAddRefnumsModal() {
    if (!this.state.showAddRefnumsModal) {
      return null;
    }
    return (
      <EditReferenceNumbersModal
        show={this.state.showAddRefnumsModal}
        reference_numbers={this.props.stop.reference_numbers}
        onClose={this.hideRefnumModal}
        onSave={this.handleSaveReferenceNumbers}
        stop={this.props.stop}
        isStop
        page
      />
    );
  }

  render() {
    const { stop, stops, loads, timezone, t } = this.props;

    return (
      <div className="mb-6 grid sm:grid-cols-12 sm:gap-x-4 sm:gap-y-6">
        <div className="sm:col-span-1">{this.props.children}</div>
        <div className="sm:col-span-2">
          <DropdownButton
            title={stop.action || t('STOP_TYPE')}
            items={this.state.stop_types}
            onSelect={(item) => {
              this.handleStopFieldChange('action', item.value);
            }}
          />
        </div>
        <div className="sm:col-span-5">
          <Location
            sequence={stop.sequence}
            location={stop.location[0]}
            locations={this.props.locations}
            onLocationFieldChange={this.handleLocationFieldChange}
            onNameSelect={this.handleLocationNameSelect}
            onAddressSelect={this.handleLocationAddressSelect}
            onLocationContactChange={this.handleLocationContactChange}
            onRemoveLocation={this.props.onRemoveLocation}
          />
        </div>
        <div className="sm:col-span-3 space-y-3">
          <div className="flex items-center justify-start gap-2 px-2 w-max">
            <Checkbox
              checked={stop?.appointment_required || false}
              className={`
                w-5 h-5 border rounded 
                cursor-pointer 
                border-gray-400
                data-[state=checked]:bg-green-600 
                data-[state=checked]:border-green-600
                transition-colors
              `}
              id={`appointment_required_${stop.sequence}`}
              defaultChecked={false}
              onCheckedChange={(value) => {
                this.handleRequireAppointmentFieldChange(
                  'appointment_required',
                  value,
                );
              }}
            />
            <Label>{t('REQUIRES_APPOINTMENT')}</Label>
          </div>
          {!stop?.appointment_required && (
            <button className="block" onClick={this.showStopAppointmentModal}>
              {!stop.window_start && !stop.window_end && (
                <IconTextDisplay
                  className="items-center"
                  icon={<CalendarIcon color="" />}
                >
                  <div className="text-sm text-red-500">
                    {t('SET_APPOINTMENT')}
                  </div>
                </IconTextDisplay>
              )}
              <div className="space-y-2">
                {stop.window_start && (
                  <IconTextDisplay color="green" icon={<CalendarIcon />}>
                    <dl className="text-left">
                      <dt className="mb-1 form-label">{t('START')}</dt>
                      <dd className="block text-sm text-gray-900">
                        {formatDate(stop.window_start, timezone, t)}
                      </dd>
                    </dl>
                  </IconTextDisplay>
                )}
                {stop.window_end && (
                  <IconTextDisplay color="red" icon={<CalendarIcon />}>
                    <dl className="text-left">
                      <dt className="mb-1 form-label">{t('END')}</dt>
                      <dd className="block text-sm text-gray-900">
                        {formatDate(stop.window_end, timezone, t)}
                      </dd>
                    </dl>
                  </IconTextDisplay>
                )}
              </div>
            </button>
          )}
          <button className="block" onClick={this.showLoadDistributionModal}>
            <IconTextDisplay icon={<TruckIcon />}>
              <div className="space-y-2">
                {stop.loads &&
                  stop.loads.length &&
                  stop.loads.map((l, i) => {
                    return (
                      <dl className="block text-sm text-left space-y-1" key={i}>
                        <dd>
                          {getLoadQuantity(l, t)}, {getLoadDimensions(l, t)}
                        </dd>
                        <dd>{getLoadWeight(l, t)}</dd>
                      </dl>
                    );
                  })}
              </div>
              {(!stop.loads || !stop.loads.length) && (
                <div className="text-sm text-red-500">
                  {t('NO_LOADS_DISTRIBUTED')}
                </div>
              )}
            </IconTextDisplay>
          </button>
          <button
            className="text-sm text-blue-500 hover:text-blue-600"
            onClick={this.showRefnumModal}
          >
            {t('ADD_STOP_REFERENCE')}
          </button>
        </div>
        {SessionStore.hasMultiStopBookingRole() && (
          <div className="sm:col-span-1">
            <Tooltip content={t('REMOVE_STOP')}>
              <DeleteButton
                className="w-5 h-5"
                onClick={this.handleRemoveStop}
              />
            </Tooltip>
          </div>
        )}
        {this.state.showStopAppointmentModal && (
          <StopAppointmentWindowModal
            show={this.state.showStopAppointmentModal}
            onClose={this.hideStopAppointmentModal}
            stop={stop}
            selected_timezone={timezone}
            onStartChange={this.handleStartChange}
            onEndChange={this.handleEndChange}
          />
        )}
        {this.state.showLoadDistributionModal && (
          <LoadDistributionModal
            show={this.state.showLoadDistributionModal}
            onClose={this.hideLoadDistributionModal}
            stops={stops}
            loads={loads}
            onSave={this.handleLoadDistributionSave}
          />
        )}
        {this.renderAddRefnumsModal()}
      </div>
    );
  }
}

export default withTranslation()(Stop);
